import { Star } from 'lucide-react';
import ChapterGridTooltip from './ChapterGridTooltip';
import { formatTotalTime, getCourseTitle } from './courseUtils';

const CourseCard = ({ course, index, language, showStats, isActive, isFavorite, onToggleFavorite, onStart, onPurchase, embedded = false }) => {
    const progress = course.progress || 0;
    const sessionProgress = course.sessionProgress || 0;
    const locked = course.requiresUpgrade;

    return (
        <div
            key={course.idCourse}
            {...(index === 0 ? { 'data-help': 'courses-card-first' } : {})}
            className={embedded
                ? `p-4 lg:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6 ${
                    locked ? 'opacity-60 grayscale pointer-events-none' : ''
                }`
                : `bg-white dark:bg-[#121212] rounded-2xl p-4 lg:p-6 border transition-all shadow-sm dark:shadow-none flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6 h-full ${
                    locked
                        ? 'opacity-60 grayscale pointer-events-none border-gray-200 dark:border-white/10'
                        : isActive
                            ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                            : 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                }`
            }
        >
            {/* Left: Title + Metrics */}
            <div className="flex-1 min-w-0">
                <h4 className="text-base lg:text-lg font-bold text-gray-900 dark:text-white mb-3 lg:mb-4 tracking-tight flex items-center gap-3 flex-wrap">
                    {onToggleFavorite && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleFavorite(course.idCourse); }}
                            className="flex-shrink-0 p-0.5 transition-colors"
                            title={isFavorite ? (language === 'fr' ? 'Retirer des favoris' : 'Remove from favorites') : (language === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites')}
                        >
                            <Star size={16} className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300 dark:hover:text-amber-500'} />
                        </button>
                    )}
                    {locked && (
                        <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    )}
                    {getCourseTitle(course, language)}
                    {locked && (
                        <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            Premium
                        </span>
                    )}
                </h4>

                {/* Metrics: Progression | Session | Temps */}
                {showStats && (
                <div className="grid grid-cols-2 lg:flex lg:flex-row gap-4 lg:gap-8">
                    <ChapterGridTooltip
                        chapterStatuses={course.chapterStatuses || []}
                        language={language}
                        isQuiz={false}
                        hasActiveSession={false}
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full">
                                <span className="text-[11px] font-black uppercase tracking-widest text-green-500 dark:text-green-400">Progression</span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">{progress}%</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]" style={{ width: `${progress}%` }}></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>

                    <ChapterGridTooltip
                        chapterStatuses={course.chapterStatuses || []}
                        language={language}
                        isQuiz={false}
                        hasActiveSession={course.hasActiveSession}
                        mode="session"
                        className="flex-1 min-w-0"
                    >
                        <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between items-center w-full">
                                <span className="text-[11px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">Session</span>
                                <span className="text-xs font-black text-gray-900 dark:text-white">
                                    {course.hasActiveSession ? `${sessionProgress}%` : '-'}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${course.hasActiveSession
                                        ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                                        : 'bg-transparent'}`}
                                    style={{ width: `${course.hasActiveSession ? sessionProgress : 0}%` }}
                                ></div>
                            </div>
                        </div>
                    </ChapterGridTooltip>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                            {language === 'fr' ? 'Temps' : 'Time'}
                        </span>
                        <span className="text-xs font-black text-gray-900 dark:text-white mt-auto">
                            {formatTotalTime(course.totalTime) || '-'}
                            {course.averageTime > 0 && (
                                <span className="ml-1 text-[11px] font-normal text-gray-400 dark:text-gray-500">
                                    (~{course.averageTime} min)
                                </span>
                            )}
                        </span>
                    </div>
                </div>
                )}
            </div>

            {/* Right: Action Button */}
            <div className="flex justify-center lg:justify-end shrink-0">
                {locked ? (
                    <button
                        onClick={onPurchase}
                        className="pointer-events-auto px-6 py-2.5 rounded-xl font-black text-xs shadow-xl transition hover:scale-105 active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 shadow-amber-500/20 border border-amber-300 dark:border-amber-500/30"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        {language === 'fr' ? 'Débloquer' : 'Unlock'}
                    </button>
                ) : (
                    <button
                        onClick={() => onStart(course)}
                        className={`px-6 py-2.5 rounded-xl font-black text-xs text-white shadow-xl transition hover:scale-105 active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap ${
                            course.hasActiveSession
                                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 shadow-yellow-500/20'
                                : course.progress > 0
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-500/20'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-blue-500/20'
                        }`}
                    >
                        {course.hasActiveSession
                            ? (language === 'fr' ? 'Continuer' : 'Continue')
                            : course.progress > 0
                                ? (language === 'fr' ? 'Revoir' : 'Review')
                                : (language === 'fr' ? 'Commencer' : 'Start')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default CourseCard;
